import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

// Fonts first — self-hosted via npm, bundled by Vite, no external requests.
import '@fontsource-variable/inter';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';

import './styles/fonts.css';
import './styles/tokens.css';
import './styles/global.css';
import './styles/components.css';
import './styles/glass.css';
import './styles/cms.css';

import { AuthProvider } from './auth/AuthProvider';
import { IdleTimeoutGuard } from './auth/IdleTimeoutGuard';
import { router } from './routes/router';
import { captureAffiliateFromUrl } from './lib/attribution';
import { CopyProvider } from './copy/CopyProvider';

// Affiliate attribution fallback, captured before React mounts.
//
// Here rather than in a route component for two reasons: the redirect can land on ANY page
// (an affiliate's base URL is whatever an admin typed), so no single route sees every
// arrival; and running it before the first render means a buyer who goes straight to
// checkout has it recorded in time.
//
// This is the second-best signal. The real one is the httpOnly cookie the engine sets on
// the click, which the engine reads back itself at checkout. See lib/attribution.ts.
captureAffiliateFromUrl();

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found.');

createRoot(container).render(
  <StrictMode>
    {/* AuthProvider wraps the router so every route, layout, and guard reads
        one shared session. */}
    <AuthProvider>
      {/* Outside the router on purpose: the timeout is a property of the SESSION, not of
          any route, so it must keep counting wherever the user has navigated to. */}
      <IdleTimeoutGuard />
      {/* Platform-scope copy for the whole app. It never blocks rendering: children paint
          immediately with their code defaults and any overrides arrive after. A page that
          needs tenant wording nests its own scoped provider, which merges over this one. */}
      <CopyProvider>
        <RouterProvider router={router} />
      </CopyProvider>
    </AuthProvider>
  </StrictMode>,
);
